import { useState } from "react";
import { NODE_ENV } from "common/env";
import { buildFullLink } from "common/routing";
import { getClientAppPrefix, getPublicPortalPrefix } from "common/utils/url";
import { useAccountsQuery } from "../../../generated/graphql";
import { AccountLogo } from "../../Common/AccountLogo";
import { ActionButton } from "../../Common/Buttons/ActionButton";
import { CopyEntityID } from "../../Common/CopyEntityID";
import { Checkbox, Text } from "../../Inputs";
import DropdownMenu, { ActionsProps } from "../../Inputs/DropdownMenu";
import {
  Account,
  AccountLinks,
  AccountName,
  AccountsGrid,
  FilterContainer,
  LogoContainer,
  LogoDefault,
  LogoHover,
  SearchBar,
  SearchContainer,
  SearchTitle,
} from "./__styles__/AccountSelector";
import { useDeleteAccountModal } from "./DeleteAccountModal";

export default function AccountSelector() {
  const isProduction = NODE_ENV === "production";
  const { error, data, loading } = useAccountsQuery({
    variables: { fetchFIRMs: !isProduction, fetchParcels: !isProduction },
  });
  const [filters, setFilters] = useState({
    searchTerm: "",
    hasParcels: false,
    hasFloodzones: false,
    hasPublicSite: false,
    isDeletable: false,
  });

  if (loading || !data)
    return (
      <SearchContainer>
        <AccountsGrid>Loading...</AccountsGrid>
      </SearchContainer>
    );

  if (error)
    return (
      <SearchContainer>
        <AccountsGrid>Error fetching accounts</AccountsGrid>
      </SearchContainer>
    );

  let accounts = data.accounts
    .filter(account =>
      account.name.toLowerCase().includes(filters.searchTerm.toLowerCase()),
    )
    .filter(({ hasParcels }) => !filters.hasParcels || hasParcels)
    .filter(({ firms }) => {
      const hasFloodzones = !!firms?.filter(
        firm => firm.components.floodzones.hasGeometries,
      ).length;
      return hasFloodzones || !filters.hasFloodzones;
    })
    .filter(
      ({ publicPortal }) => !filters.hasPublicSite || publicPortal.enabled,
    )
    .filter(({ isDeletable }) => !filters.isDeletable || isDeletable);

  return (
    <SearchContainer>
      <SearchBar>
        <SearchTitle>Select an account</SearchTitle>
        <Text
          name="Search"
          value={filters.searchTerm}
          onChange={searchTerm => setFilters({ ...filters, searchTerm })}
          placeholder="Search for an account"
          size="medium"
        />
        {!isProduction && (
          <FilterContainer>
            <Checkbox
              name="hasParcels"
              value={filters.hasParcels}
              onChange={hasParcels => setFilters({ ...filters, hasParcels })}
              label="has parcels"
            />
            <Checkbox
              name="hasFloodzones"
              value={filters.hasFloodzones}
              onChange={hasFloodzones =>
                setFilters({ ...filters, hasFloodzones })
              }
              label="has floodzones"
            />
            <Checkbox
              name="hasPublicSite"
              value={filters.hasPublicSite}
              onChange={hasPublicSite =>
                setFilters({ ...filters, hasPublicSite })
              }
              label="has public site"
            />
            <Checkbox
              name="isDeletable"
              value={filters.isDeletable}
              onChange={isDeletable => setFilters({ ...filters, isDeletable })}
              label="is deletable"
            />
          </FilterContainer>
        )}
      </SearchBar>
      <AccountsGrid>
        {accounts.map(account => {
          return <AccountCard key={account.id} account={account} />;
        })}
      </AccountsGrid>
    </SearchContainer>
  );
}

const AccountCard = ({
  account,
}: {
  account: NonNullable<
    ReturnType<typeof useAccountsQuery>["data"]
  >["accounts"][0];
}) => {
  const portalUrl = buildFullLink("guestHome", {
    prefix: getPublicPortalPrefix(account.subdomain),
    params: {},
  });

  const prefix = getClientAppPrefix(account.subdomain);

  const clientAppUrl = buildFullLink("map", {
    prefix,
    params: {},
  });

  const showDeleteModal = useDeleteAccountModal({
    id: account.id,
    name: account.name,
  });

  const actions: Array<ActionsProps> = [];

  if (account.publicPortal.enabled) {
    actions.push({
      label: "Public portal",
      href: portalUrl,
      target: "_blank",
    });
  }

  if (account.isDeletable) {
    actions.push({
      label: "Delete account",
      onClick: () => {
        showDeleteModal();
      },
      variant: "red",
    });
  }

  return (
    <Account
      className="account-card"
      style={{ backgroundColor: account.publicPortal.color }}
      onClick={() => {
        window.location.href = clientAppUrl;
      }}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          window.location.href = clientAppUrl;
        }
      }}
      aria-label={`Open ${account.name} client app`}
    >
      <LogoContainer>
        {account.logoUrl && (
          <LogoDefault>
            <AccountLogo size={28} account={account} />
          </LogoDefault>
        )}
        <LogoHover
          onClick={e => {
            e.stopPropagation();
          }}
        >
          <CopyEntityID
            entityId={account.id}
            tooltipText="Copy account ID"
            variant="inverted"
          />
        </LogoHover>
      </LogoContainer>
      <AccountName>{account.name}</AccountName>
      <AccountLinks className="links" onClick={e => e.stopPropagation()}>
        {actions.length > 0 && (
          <DropdownMenu
            actions={actions}
            customButton={({ onPress, disabled }) => (
              <ActionButton
                onPress={onPress}
                isDisabled={disabled}
                variant="inverted"
                aria-label="Account actions"
              />
            )}
          />
        )}
      </AccountLinks>
    </Account>
  );
};
